import { useFormikContext } from "formik";
import React, { useRef, useState } from "react";
import CalendarRange from "@components/CalendarRange";
import { FilterIconBar, FilterIconBarMobile } from "@components/Icons";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  createStyles,
  makeStyles,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { useFormQuery } from "./FormQueryContext";
import { IQuery } from "./interface";

import { useUserState } from "@context/UserContext";

import { optionsFinancialStatus } from "@utils/MyOrders/ENUM_STATUS_ORDER";
import { capitalizeFirstLetterFullString, maskCNPJ } from "@utils/index";
import { LocalStorageKeysOrders } from "./utilsOrders/localStorageOrders";
import { endOfMonth, startOfMonth } from "date-fns";

const useStyles = makeStyles((theme) =>
  createStyles({
    centeredText: {
      textAlign: "center",
    },
    formControl: {
      minWidth: 120,
    },
    selectEmpty: {
      marginTop: theme.spacing(2),
    },
    selectInput: {
      borderRadius: "8px",
      "&:focus": {
        backgroundColor: "#FFFF",
      },
    },
    itemSelect: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
    },
    itemSelectTitle: {
      fontSize: "1.6rem",
      fontWeight: "bold",
    },
    itemSelectSubTitle: {
      fontSize: "1.2rem",
    },
    iconsDisabled: {
      opacity: "0.12",
    },
    iconNotDisabled: {
      opacity: "0.6",
    },
    selectedChip: {
      backgroundColor: "#ffff",
      border: "1px solid #D91F05",
      color: "#D91F05",
      "&:focus": {
        backgroundColor: "#ffff",
        border: "1px solid #D91F05",
      },
    },
    labelChip: {
      fontFamily: "Montserrat",
      marginBottom: "0.5rem",
      color: "#3E3D3D",
      fontWeight: "bold",
      fontSize: "24px",
    },
    chip: {
      margin: "1rem 1rem 1rem 0px",
      borderRadius: "8px",
    },
    chipNotSelected: {
      border: "1px solid #3E3D3D",
      background: "#fff",
      "&:hover": {
        backgroundColor: theme.palette.primaryLight.main,
        color: "#D91F05",
        border: "1px solid #D91F05",
      },
    },
    labelOfInput: {
      fontWeight: "bold",
      fontFamily: "Montserrat",
      color: "#3E3D3D",
      fontSize: "16px",
      textAlign: "left",
    },
    customSelect: {
      justifyContent: "center",
      alignItems: "center",
      display: "flex",
      marginTop: "0",
      height: "100%",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      fontFamily: "Montserrat",
      fontSize: "16px",
      fontWeight: 400,
      paddingLeft: "14px",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(114, 101, 81, 0.20)",
      },
      "& .MuiSvgIcon-root": {
        fill: "#D91F05",
      },
      "& .MuiSelect-outlined.MuiSelect-outlined": {
        paddingRight: "45px",
      },
      borderRadius: "8px",
    },
    checkbox: {
      padding: 4,
      "& .MuiSvgIcon-root": {
        width: 18,
        height: 18,
      },
    },
    customButtonOutlined: {
      borderRadius: "2px",
      textTransform: "none",
      fontWeight: "bold",
      fontFamily: "Montserrat",
      width: "131.14px",
      height: "48px",
      marginTop: "10px",
      marginBottom: "10px",
      boxShadow: "none",
    },
    customButtonContained: {
      borderRadius: "2px",
      textTransform: "none",
      fontWeight: "bold",
      fontFamily: "Montserrat",
      background: "#D91F05",
      width: "131.14px",
      height: "48px",
      marginTop: "10px",
      marginBottom: "10px",
      boxShadow: "none",
    },
  })
);

export interface DrawerFilterState {
  category: string[];
}

export interface FormQueryProps {
  state?: DrawerFilterState;
  setState?: (data: DrawerFilterState) => void;
  setOpen?: boolean;
}

const FormQuery: React.FC<FormQueryProps> = () => {
  const classes = useStyles();
  const {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<IQuery>();

  const userState = useUserState();
  const formQueryContext = useFormQuery();
  const refCNPJ = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));
  const [isTooltipOpenCNPJ, setTooltipOpenCNPJ] = useState(false);
  const [isTooltipOpenStatusSale, setTooltipOpenStatusSale] = useState(false);

  const handleTooltipOpenStatusSale = () => setTooltipOpenStatusSale(true);
  const handleTooltipOpenCNPJ = () => setTooltipOpenCNPJ(true);
  const handleTooltipCloseCNPJ = () => setTooltipOpenCNPJ(false);
  const handleTooltipCloseStatusSale = () => setTooltipOpenStatusSale(false);

  const clearCNPJField = async () => {
    try {
      await setFieldValue("CNPJ", []);
      setFieldTouched("CNPJ", false);
    } catch (error) {
      console.error("Erro ao limpar o campo CNPJ:", error);
    }
  };

  const clearStatusSaleField = async () => {
    try {
      await setFieldValue("statusSale", []);
      setFieldTouched("statusSale", false);
    } catch (error) {
      console.error("Erro ao limpar o campo statusSale:", error);
    }
  };

  const clearDateRangeField = async () => {
    try {
      const firstDayOfMonth = startOfMonth(new Date());
      const lastDayOfMonth = endOfMonth(new Date());
      const dateRange: [Date, Date] = [firstDayOfMonth, lastDayOfMonth];

      await setFieldValue("dateRange", dateRange);
      setFieldTouched("startDate", true);
      setFieldTouched("endDate", true);
    } catch (error) {
      console.error("Erro ao limpar o campo de data:", error);
    }
  };

  const clearFilter = async () => {
    await Promise.all([
      clearCNPJField(),
      clearStatusSaleField(),
      clearDateRangeField(),
    ]);
  };

  const renderCNPJ = () => {
    return userState.listCNPJ.map((ele, idx) => {
      return (
        <MenuItem
          key={ele.BpID ? ele.BpID : ele.CNPJ}
          value={ele.BpID ? ele.BpID : ele.CNPJ}
          className={classes.itemSelect}
        >
          <Grid container>
            <Grid item>
              <Checkbox
                className={!isMobile ? undefined : classes.checkbox}
                style={{
                  color: "#D91F05",
                  width: isMobile ? "10px" : "",
                  paddingLeft: isMobile ? "5px" : "",
                  marginRight: isMobile ? "10px" : "",
                }}
                checked={
                  ele.BpID
                    ? values.CNPJ.indexOf(ele.BpID) > -1
                    : values.CNPJ.indexOf(ele.CNPJ) > -1
                }
              />
            </Grid>
            <Grid item>
              <Typography
                className={classes.itemSelectTitle}
                style={{ fontSize: isMobile ? "12px" : "" }}
              >
                {capitalizeFirstLetterFullString(ele.companyName)}
              </Typography>
              <Typography className={classes.itemSelectSubTitle}>
                {maskCNPJ(ele.CNPJ)}
              </Typography>
              {ele.BpID && (
                <Typography className={classes.itemSelectSubTitle}>
                  BP: {ele.BpID}
                </Typography>
              )}
            </Grid>
          </Grid>
        </MenuItem>
      );
    });
  };

  const clickedInFilter = () => {
    if (values.CNPJ.length > 0 && values.statusSale.length > 0) {
      // Quando o usuário clica em filtrar então limpa os filtros retornados da tela de Novo Pedido
      localStorage.removeItem(LocalStorageKeysOrders.CNPJFromNewOrder);
      localStorage.removeItem(LocalStorageKeysOrders.DateDelivery);
      localStorage.setItem("isOrderFromNewRequest", "false");
      formQueryContext.setIsClickedFilter(true);
    } else {
      refCNPJ.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const renderStatusSale = () => {
    return optionsFinancialStatus.map((ele, idx) => {
      return (
        <MenuItem key={ele.value} value={ele.value}>
          <Grid container alignItems="center">
            <Grid item>
              <Checkbox
                className={!isMobile ? undefined : classes.checkbox}
                style={{
                  color: "#D91F05",
                  paddingLeft: isMobile ? "5px" : "",
                  marginRight: isMobile ? "10px" : "",
                  verticalAlign: "middle",
                  fontSize: "10px",
                }}
                checked={values.statusSale.indexOf(ele.value) > -1}
              />
            </Grid>
            <Grid item>
              <Typography
                style={{ fontSize: isMobile ? "12px" : "", fontWeight: 500 }}
              >
                {ele.label}
              </Typography>
            </Grid>
          </Grid>
        </MenuItem>
      );
    });
  };

  return (
    <Grid container justifyContent="center" alignItems="center" spacing={2}>
      <Grid item xs={10}>
        <InputLabel
          className={classes.labelChip}
          style={{ fontSize: isMobile ? "1.6rem" : "" }}
        >
          Filtrar dados
        </InputLabel>
      </Grid>
      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        style={{ padding: "30px 0" }}
      >
        <Grid item xs={3}>
          {isMobile ? <FilterIconBarMobile /> : <FilterIconBar />}
        </Grid>
      </Grid>
      <Grid item xs={10} ref={refCNPJ}>
        <FormControl
          className={classes.formControl}
          variant="outlined"
          fullWidth
        >
          <InputLabel
            htmlFor="outlined-razao-social-native-simple"
            style={{ fontWeight: !!touched.CNPJ ? "bold" : "none" }}
          >
            Razão social
          </InputLabel>
          <Tooltip
            open={
              values.CNPJ && values.CNPJ.length > 0 ? isTooltipOpenCNPJ : false
            }
            onClose={handleTooltipCloseCNPJ}
            title={
              values.CNPJ &&
              values.CNPJ.length > 0 && (
                <div>
                  {(values.CNPJ as string[]).map((value) => (
                    <Typography key={value}>
                      {capitalizeFirstLetterFullString(
                        userState.listCNPJ.find((CNPJ) =>
                          CNPJ.BpID ? CNPJ.BpID === value : CNPJ.CNPJ === value
                        )?.companyName
                      )}
                    </Typography>
                  ))}
                </div>
              )
            }
          >
            <div
              onMouseEnter={handleTooltipOpenCNPJ}
              onMouseLeave={handleTooltipCloseCNPJ}
            >
              <Select
                className={classes.customSelect}
                label="Razão social"
                error={!!errors.CNPJ && !!touched.CNPJ}
                onBlur={() => setFieldTouched("CNPJ", true)}
                value={values.CNPJ}
                style={{
                  paddingLeft: isMobile ? "0px" : "",
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  getContentAnchorEl: null,
                }}
                multiple
                inputProps={{
                  name: "razao-social",
                  id: "outlined-razao-social-native-simple",
                }}
                renderValue={(selected) => (
                  <div
                    style={{
                      display: "flex",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {(selected as string[]).map((value) => {
                      return (
                        <Typography
                          style={{
                            fontSize: isMobile ? "1.2rem" : "",
                          }}
                        >
                          {capitalizeFirstLetterFullString(
                            userState.listCNPJ.find((CNPJ) =>
                              CNPJ.BpID
                                ? CNPJ.BpID === value
                                : CNPJ.CNPJ === value
                            )?.companyName
                          )}
                          {(selected as string[]).length > 1 && " / "}
                        </Typography>
                      );
                    })}
                  </div>
                )}
                onChange={({ target }) => {
                  setFieldValue("CNPJ", target.value);
                }}
                name="CNPJ"
                IconComponent={(props) => (
                  <ExpandMoreIcon
                    {...props}
                    style={{
                      marginRight: !isMobile ? "15px" : "",
                    }}
                  />
                )}
              >
                {renderCNPJ()}
              </Select>
            </div>
          </Tooltip>

          <FormHelperText
            error={!!errors.CNPJ && !!touched.CNPJ}
            style={{ fontSize: isMobile ? "1.1rem" : "" }}
          >
            {!!touched.CNPJ && errors.CNPJ}
          </FormHelperText>
        </FormControl>
      </Grid>
      <Grid item xs={10}>
        <CalendarRange
          //@ts-ignore
          style={{ height: "30px" }}
          name="dateRange"
          label="Período de Entrega"
          value={values.dateRange}
          onChange={(dateRange) => {
            setFieldValue("dateRange", dateRange);
            setFieldTouched("startDate", true);
            setFieldTouched("endDate", true);
          }}
          onBlur={() => {
            setFieldTouched("startDate", true);
            setFieldTouched("endDate", true);
          }}
        />
      </Grid>
      <Grid item xs={10}>
        <Typography
          className={classes.labelOfInput}
          style={{
            fontSize: isMobile ? "1.4rem" : "",
            paddingBottom: isMobile ? "0px" : "",
          }}
        >
          <FormControl
            className={classes.formControl}
            variant="outlined"
            fullWidth
          >
            <InputLabel
              htmlFor="outlined-status-pedido-native-simple"
              style={{ fontWeight: !!touched.statusSale ? "bold" : "none" }}
            >
              Status do Pedido
            </InputLabel>
            <Tooltip
              open={
                values.statusSale && values.statusSale.length > 0
                  ? isTooltipOpenStatusSale
                  : false
              }
              onClose={handleTooltipCloseStatusSale}
              title={
                values.statusSale &&
                values.statusSale.length > 0 && (
                  <div>
                    {(values.statusSale as string[]).map((value) => (
                      <Typography key={value}>
                        {
                          optionsFinancialStatus.find(
                            (indexStatusSale) => indexStatusSale.value === value
                          )?.label
                        }
                      </Typography>
                    ))}
                  </div>
                )
              }
            >
              <div
                onMouseEnter={handleTooltipOpenStatusSale}
                onMouseLeave={handleTooltipCloseStatusSale}
              >
                <Select
                  className={classes.customSelect}
                  label="Status do pedido"
                  error={!!errors.statusSale && !!touched.statusSale}
                  onBlur={() => setFieldTouched("statusSale", true)}
                  value={values.statusSale}
                  style={{
                    paddingLeft: isMobile ? "0px" : "",
                  }}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    getContentAnchorEl: null,
                  }}
                  multiple
                  inputProps={{
                    name: "status-pedido",
                    id: "outlined-status-pedido-native-simple",
                  }}
                  renderValue={(selected) => (
                    <div
                      style={{
                        display: "flex",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {(selected as string[]).map((value) => {
                        return (
                          <Typography
                            style={{ fontSize: isMobile ? "1.2rem" : "" }}
                          >
                            {
                              optionsFinancialStatus.find(
                                (index) => index.value === value
                              )?.label
                            }
                            {(selected as string[]).length > 1 && " / "}
                          </Typography>
                        );
                      })}
                    </div>
                  )}
                  onChange={({ target }) => {
                    setFieldValue("statusSale", target.value);
                  }}
                  name="statusSale"
                  IconComponent={(props) => (
                    <ExpandMoreIcon
                      {...props}
                      style={{
                        marginRight: !isMobile ? "15px" : "",
                      }}
                    />
                  )}
                >
                  {renderStatusSale()}
                </Select>
              </div>
            </Tooltip>

            <FormHelperText
              error={!!errors.statusSale && !!touched.statusSale}
              style={{ fontSize: isMobile ? "1.1rem" : "" }}
            >
              {!!touched.statusSale && errors.statusSale}
            </FormHelperText>
          </FormControl>
        </Typography>
      </Grid>

      <Grid
        container
        justifyContent="center"
        alignItems="center"
        spacing={0}
        className={classes.centeredText}
      >
        <Grid item xs={10}>
          <Button
            color="primary"
            type="submit"
            variant="contained"
            className={classes.customButtonContained}
            startIcon={
              formQueryContext.loadingQuery && (
                <CircularProgress
                  color="secondary"
                  style={{ height: 20, width: 20 }}
                />
              )
            }
            onClick={clickedInFilter}
          >
            Filtrar
          </Button>
        </Grid>

        <Grid item xs={10}>
          <Button
            color="primary"
            type="submit"
            variant="outlined"
            className={classes.customButtonOutlined}
            startIcon={
              formQueryContext.loadingQuery && (
                <CircularProgress
                  color="secondary"
                  style={{ height: 20, width: 20 }}
                />
              )
            }
            onClick={clearFilter}
          >
            Limpar filtros
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default FormQuery;
